import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AuthService } from '../services/auth.service';

export interface HasUnsavedChanges {
  hasUnsavedChanges(): boolean;
  /** Lưu toàn bộ thay đổi đang dở; resolve `true` khi lưu xong, `false` khi lỗi (đã tự báo lỗi). */
  saveUnsavedChanges?(): Promise<boolean>;
}

export interface UnsavedChangesDialogOptions {
  content?: string;
  /** Không truyền thì popup chỉ có "Ở lại" / "Rời trang" như trước. */
  save?: () => Promise<boolean>;
}

const DEFAULT_CONTENT = 'Nếu rời trang bây giờ, các thay đổi chưa lưu sẽ bị mất. Bạn vẫn muốn rời trang?';

/**
 * Popup "Còn dữ liệu chưa lưu" dùng chung cho rời trang (guard route) và chuyển tab/sheet trong trang.
 * Resolve `true` = cho đi tiếp (đã lưu xong hoặc chọn bỏ thay đổi), `false` = ở lại.
 * Bấm "Lưu" mà lưu lỗi thì đóng popup và giữ người dùng ở lại - thông báo lỗi do hàm `save` hiển thị.
 */
export function confirmUnsavedChanges(modal: NzModalService, options: UnsavedChangesDialogOptions = {}): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let saving = false;
    let decided = false;
    const decide = (leave: boolean): void => {
      if (!decided) {
        decided = true;
        resolve(leave);
      }
    };

    const ref = modal.create({
      nzTitle: 'Còn dữ liệu chưa lưu',
      nzContent: options.content ?? DEFAULT_CONTENT,
      nzWidth: 460,
      nzMaskClosable: false,
      nzKeyboard: false,
      // Đang lưu thì không cho đóng bằng nút X, tránh request lưu chạy ngầm sau khi đã "ở lại".
      nzOnCancel: () => (saving ? false : decide(false)),
      nzFooter: [
        {
          label: 'Ở lại',
          disabled: () => saving,
          onClick: () => {
            decide(false);
            ref.destroy();
          }
        },
        {
          label: 'Rời trang',
          type: 'primary',
          danger: true,
          disabled: () => saving,
          onClick: () => {
            decide(true);
            ref.destroy();
          }
        },
        ...(options.save
          ? [{
            label: 'Lưu',
            type: 'primary' as const,
            // autoLoading (mặc định) hiện spinner trên nút trong lúc promise chưa xong.
            onClick: async () => {
              saving = true;
              let ok = false;
              try {
                ok = await options.save!();
              } catch {
                ok = false;
              } finally {
                saving = false;
              }
              decide(ok);
              ref.destroy();
            }
          }]
          : [])
      ]
    });
    ref.afterClose.subscribe(() => decide(false));
  });
}

/** Hỏi xác nhận trước khi rời một trang còn dữ liệu chưa lưu. */
export const unsavedChangesGuard: CanDeactivateFn<HasUnsavedChanges> = (component) => {
  // Người dùng đã chọn đăng xuất (phiên đã bị xoá) thì không hỏi lại.
  if (!inject(AuthService).isAuthenticated() || !component?.hasUnsavedChanges()) {
    return true;
  }

  return confirmUnsavedChanges(inject(NzModalService), {
    save: component.saveUnsavedChanges ? () => component.saveUnsavedChanges!() : undefined
  });
};
